import { useState, useCallback } from "react";
import { MessageSquare, ChevronDown, Loader2, Trash2, Phone, Mail, Users, StickyNote, Send, Sparkles, Copy } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const TYPES = [
  { key: "call", label: "Hovor", icon: Phone, cls: "text-emerald-400 bg-emerald-500/15" },
  { key: "email", label: "E-mail", icon: Mail, cls: "text-sky-400 bg-sky-500/15" },
  { key: "meeting", label: "Schůzka", icon: Users, cls: "text-orange-400 bg-orange-500/15" },
  { key: "note", label: "Poznámka", icon: StickyNote, cls: "text-zinc-400 bg-white/10" },
];
const fmt = (s) => (s ? new Date(s).toLocaleString("cs-CZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const CommunicationLog = ({ clientId, token }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("call");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const summarize = async () => {
    setAiLoading(true);
    try {
      const log = items.map((c) => `[${(TYPES.find((t) => t.key === c.type) || TYPES[3]).label}] ${c.text}`).join("\n");
      const r = await apiFetch("/admin/ai/assist", { method: "POST", token, body: { mode: "summarize", text: `Komunikace s klientem:\n${log}\n\nShrň klíčové body a navrhni další krok.` } });
      setAiSummary(r.result || "");
    } catch (e) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await apiFetch(`/admin/clients/${clientId}/communications`, { token })); setLoaded(true); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [clientId, token]);

  const toggle = () => { const n = !open; setOpen(n); if (n && !loaded) load(); };

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Zadejte text."); return; }
    setSaving(true);
    try { await apiFetch(`/admin/clients/${clientId}/communications`, { method: "POST", token, body: { type, text } }); setText(""); toast.success("Záznam přidán."); load(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Smazat záznam komunikace?", confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/communications/${id}`, { method: "DELETE", token }); toast.success("Smazáno."); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`comm-log-${clientId}`}>
      <button onClick={toggle} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid={`comm-toggle-${clientId}`}>
        <MessageSquare className="w-3.5 h-3.5 text-orange-400" /> Komunikace {loaded ? `(${items.length})` : ""} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <form onSubmit={add} className="flex flex-col sm:flex-row gap-2" data-testid={`comm-form-${clientId}`}>
            <select value={type} onChange={(e) => setType(e.target.value)} className="input-dark sm:w-32" data-testid={`comm-type-${clientId}`}>
              {TYPES.map((t) => <option key={t.key} value={t.key} className="bg-[#0a0a0a]">{t.label}</option>)}
            </select>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Co se řešilo…" className="input-dark flex-1" data-testid={`comm-text-${clientId}`} />
            <button type="submit" disabled={saving} className="btn-accent inline-flex items-center justify-center gap-1.5 disabled:opacity-60 shrink-0" data-testid={`comm-submit-${clientId}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Zaznamenat
            </button>
          </form>

          {items.length > 0 && (
            <button onClick={summarize} disabled={aiLoading} className="text-[11px] px-2.5 py-1 rounded-full border border-orange-500/30 text-orange-300 hover:bg-orange-500/10 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`comm-ai-${clientId}`}>
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI: shrnout & navrhnout další krok
            </button>
          )}
          {aiSummary && (
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3" data-testid={`comm-ai-result-${clientId}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-orange-300 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI shrnutí</span>
                <button onClick={() => { navigator.clipboard.writeText(aiSummary); toast.success("Zkopírováno."); }} className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid={`comm-ai-copy-${clientId}`}><Copy className="w-3 h-3" /> Kopírovat</button>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{aiSummary}</p>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-orange-400 animate-spin" /></div>
          ) : items.length === 0 ? (
            <p className="text-xs text-zinc-500 py-2">Zatím žádná zaznamenaná komunikace.</p>
          ) : (
            <div className="space-y-2" data-testid={`comm-list-${clientId}`}>
              {items.map((c) => {
                const m = TYPES.find((t) => t.key === c.type) || TYPES[3];
                const Icon = m.icon;
                return (
                  <div key={c.id} className="flex items-start gap-3 text-sm bg-white/5 rounded-lg p-3" data-testid={`comm-${c.id}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${m.cls}`}><Icon className="w-3.5 h-3.5" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-zinc-200 break-words">{c.text}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{m.label} · {fmt(c.created_at)}{c.created_by_name ? ` · ${c.created_by_name}` : ""}</p>
                    </div>
                    <button onClick={() => remove(c.id)} className="text-zinc-600 hover:text-red-400 p-1 shrink-0" data-testid={`comm-delete-${c.id}`} aria-label="Smazat"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunicationLog;
